import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BrandAvatar } from "@/components/BrandAvatar";
import { Link2, Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";

/** Declara que varias cuentas son de la misma persona (solo superadmin). */
export const LinkedAccountsPanel = ({ userId }) => {
  const { t } = useI18n();
  const [linked, setLinked] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [buscando, setBuscando] = useState(false);
  const [q, setQ] = useState("");
  const [todos, setTodos] = useState([]);
  const [guardando, setGuardando] = useState("");

  useEffect(() => {
    setCargando(true);
    api.get(`/users/${userId}/links`)
      .then((r) => setLinked(r.data?.accounts || []))
      .catch(() => setLinked([]))
      .finally(() => setCargando(false));
  }, [userId]);

  useEffect(() => {
    if (!buscando || todos.length) return;
    api.get("/users", { params: { fields: "user_id,name,email,role" } })
      .then((r) => setTodos(r.data || []))
      .catch(() => setTodos([]));
  }, [buscando, todos.length]);

  const candidatos = useMemo(() => {
    const texto = q.trim().toLowerCase();
    if (texto.length < 2) return [];
    const yaEstan = new Set([userId, ...linked.map((a) => a.user_id)]);
    return todos
      .filter((u) => !yaEstan.has(u.user_id))
      .filter((u) => `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(texto))
      .slice(0, 6);
  }, [q, todos, linked, userId]);

  const vincular = async (u) => {
    setGuardando(u.user_id);
    try {
      const r = await api.post(`/users/${userId}/links`, { user_id: u.user_id });
      setLinked(r.data?.accounts || []);
      setQ(""); setBuscando(false);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally { setGuardando(""); }
  };

  const quitar = async (a) => {
    setGuardando(a.user_id);
    try {
      const r = await api.delete(`/users/${userId}/links/${a.user_id}`);
      setLinked(r.data?.accounts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("error_generic"));
    } finally { setGuardando(""); }
  };

  return (
    <div data-testid="linked-accounts-panel">
      <div className="flex items-center gap-2 mb-2.5">
        <div className="h-px flex-1 bg-slate-200" />
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          <Link2 className="h-3.5 w-3.5" />
          {t("sw_link_title")}
        </div>
        <div className="h-px flex-1 bg-slate-200" />
      </div>

      {cargando ? (
        <p className="text-xs text-slate-500 text-center py-3"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></p>
      ) : (
        <>
          {linked.length === 0 ? (
            <p className="text-[12px] text-slate-500 mb-2">{t("sw_link_hint")}</p>
          ) : (
            <div className="flex flex-col gap-1.5 mb-2">
              {linked.map((a) => (
                <div key={a.user_id} className="flex items-center gap-2.5 border border-slate-200 rounded-lg px-3 py-2"
                     data-testid={`linked-account-${a.user_id}`}>
                  <BrandAvatar src={a.picture} alt={a.name} className="h-7 w-7 shrink-0" />
                  <span className="flex flex-col min-w-0 flex-1 leading-tight">
                    <span className="text-[13px] font-semibold truncate">{t(a.role) || a.role}</span>
                    <span className="text-[11px] text-slate-500 truncate">{a.email}</span>
                  </span>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 shrink-0"
                          disabled={guardando === a.user_id} onClick={() => quitar(a)}
                          data-testid={`unlink-account-${a.user_id}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {!buscando ? (
            <Button size="sm" variant="outline" onClick={() => setBuscando(true)} data-testid="link-account-open">
              <Plus className="h-3.5 w-3.5" /> {t("sw_link_add")}
            </Button>
          ) : (
            <div>
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder={`${t("search")}…`} className="h-9 text-[13px]"
                     data-testid="link-account-search" />
              <div className="flex flex-col gap-1 mt-1.5">
                {candidatos.map((u) => (
                  <button key={u.user_id} type="button" disabled={!!guardando} onClick={() => vincular(u)}
                          className="flex items-center gap-2 text-left border border-slate-200 rounded-lg px-3 py-2 hover:border-brand-blue transition-colors"
                          data-testid={`link-candidate-${u.user_id}`}>
                    <span className="flex flex-col min-w-0 flex-1 leading-tight">
                      <span className="text-[13px] font-medium truncate">{u.name}</span>
                      <span className="text-[11px] text-slate-500 truncate">{u.email} · {t(u.role) || u.role}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LinkedAccountsPanel;
